/**
 * familyActivity.js — the family activity list is OFF unless the server says otherwise.
 *
 * **Why one module for two lines of logic.** The recovered code answered this question twice and
 * got it wrong both times, in opposite files, in ways that disclose one child's activity to
 * another:
 *
 *   - `Insights.jsx:789` read `leaderboardData.enabled !== false`. A failed request, an empty
 *     object, a `null`, or a non-JSON 200 is "not false", so every one of those RENDERED the list.
 *   - `Settings.jsx` shipped the switch `defaultChecked: true` while its own description called
 *     the feature "Opt-in per family".
 *
 * One home means the rule cannot drift apart again.
 *
 * **The rule.** Only the boolean `true` is on. Not `'true'`, not `1`, not "the key is missing so
 * probably fine". This matches `tenants.family_activity_enabled boolean NOT NULL DEFAULT false`
 * from the F-30 migration and the design the owner accepted on 2026-09-08: unranked, ordered by
 * member name, whole family including adults, **default OFF**.
 *
 * **The server enforces the same gate.** `GET /api/family-activity` (formerly
 * `GET /api/achievements/leaderboard`, now 410 Gone),
 * `GET /api/reports/family-trends` and `GET /api/reports/family-summary` consult
 * `tenants.family_activity_enabled` and emit the boolean `enabled`. With the setting off, the
 * family activity returns `{ enabled: false, rows: [] }`, family trends returns
 * `{ enabled: false, data: [] }` alongside its query options, and the family summary returns
 * `{ enabled: false, familyTotals: null, rows: [] }`. None of them reads member activity while the
 * setting is off. The live Insights container reads its approved-XP extension;
 * insightsMapping.js preserves the server's name order and qualifies incomplete reporting
 * coverage. The gallery uses explicit synthetic data, and this module applies the same explicit
 * boolean rule to every family activity payload its callers supply.
 *
 * **The server withholds the rows, not the browser.** When the setting is off the endpoint answers
 * `{ enabled: false, rows: [] }`. `familyActivityRows` still refuses to hand rows out when the
 * list is off, which is defence in depth against a server that regresses — not a substitute for
 * the server doing its half.
 */

/** True only when the payload says so with the actual boolean `true`. */
export function familyActivityIsOn(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;
  return payload.enabled === true;
}

/**
 * The rows to show, which is `[]` for every case except an explicitly enabled payload.
 *
 * Entries that are not objects are dropped, so one bad row cannot fault the render and take the
 * whole page down with it.
 */
export function familyActivityRows(payload) {
  if (!familyActivityIsOn(payload)) return [];
  const { rows } = payload;
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => row && typeof row === 'object' && !Array.isArray(row));
}
